/**
 * Hook to fetch geographical data such as countries, states, and cities.
 *
 * Purpose: Provide easy access to geographic data for components
 * Created: January 22, 2026
 * Updated: January 22, 2026 - Initial implementation
 */

import {
  useQuery,
  QueryClient,
  useMutation,
  UseQueryResult,
} from '@tanstack/react-query';
import { GeoDataService } from '../services/GeoDataService';
import { GeoCity, GeoState, GeoCountry, GeoStats } from '@xanectech/types';
import { createLogger } from '@xanectech/lib';
import { toast } from '@xanectech/ui/lib';

const logger = createLogger('Data - useGeoData');

export interface AllGeoDataResult {
  countries: GeoCountry[];
  states: GeoState[];
  cities: GeoCity[];
}

export const useAllGeoData = (): UseQueryResult<AllGeoDataResult, Error> => {
  return useQuery<AllGeoDataResult, Error>({
    queryKey: ['geo', 'all'],
    queryFn: async (): Promise<AllGeoDataResult> => {
      const [countries, states, cities] = await Promise.all([
        GeoDataService.getCountries(),
        GeoDataService.getStates(),
        GeoDataService.getCities(),
      ]);
      return { countries, states, cities };
    },
    staleTime: 60 * 60 * 1000, // 1 hour in ms
  });
};

export const useGetCountryEmojiByDialingCode = (
  dialingCode: string,
): UseQueryResult<string | null, Error> => {
  return useQuery<string | null, Error>({
    queryKey: ['geo', 'countryEmoji', dialingCode],
    queryFn: async (): Promise<string | null> => {
      const country = await GeoDataService.getCountryByDialingCode(dialingCode);
      return country ? country.emoji : null;
    },
    enabled: !!dialingCode,
  });
};

export const useGeoStats = () => {
  return useQuery<GeoStats>({
    queryKey: ['geo', 'geoStats'],
    queryFn: async (): Promise<GeoStats> => {
      return await GeoDataService.getGeoStats();
    },
  });
};

export const useAllCountries = (): UseQueryResult<GeoCountry[] | Error> => {
  return useQuery<GeoCountry[]>({
    queryKey: ['geo', 'countries'],
    queryFn: async () => {
      const countries = await GeoDataService.getCountries();
      return countries;
    },
  });
};

export const useCountryById = (
  countryId: string,
): UseQueryResult<GeoCountry | Error> => {
  return useQuery<GeoCountry>({
    queryKey: ['geo', 'countries', countryId],
    queryFn: async (): Promise<GeoCountry> => {
      const [country] = await Promise.all([
        GeoDataService.getCountryById(countryId),
      ]);
      if (!country) {
        throw new Error(`Country with id ${countryId} not found`);
      }
      return country;
    },
    enabled: !!countryId,
  });
};

export const useStatesByCountryCode = (
  countryCode: string,
): UseQueryResult<GeoState[] | Error> => {
  return useQuery<GeoState[]>({
    queryKey: ['geo', 'states', countryCode],
    queryFn: async () => {
      const states = await GeoDataService.getStatesByCountryCode(countryCode);
      return states;
    },
    enabled: !!countryCode,
  });
};

export const useStatesByCountryId = (
  countryId: string,
): UseQueryResult<GeoState[] | Error> => {
  return useQuery<GeoState[]>({
    queryKey: ['geo', 'states', countryId],
    queryFn: async () => {
      const states = await GeoDataService.getStatesByCountryId(countryId);
      return states;
    },
    enabled: !!countryId,
  });
};

export const useAllStates = (): UseQueryResult<GeoState[] | Error> => {
  return useQuery<GeoState[]>({
    queryKey: ['geo', 'states'],
    queryFn: async () => {
      const states = await GeoDataService.getStates();
      return states;
    },
  });
};

export const useAllCities = (): UseQueryResult<GeoCity[] | Error> => {
  return useQuery<GeoCity[]>({
    queryKey: ['geo', 'cities'],
    queryFn: async () => {
      const cities = await GeoDataService.getCities();
      return cities;
    },
  });
};

export const useCitiesByStateId = (
  stateId: string,
): UseQueryResult<GeoCity[] | Error> => {
  return useQuery<GeoCity[]>({
    queryKey: ['geo', 'cities', stateId],
    queryFn: async () => {
      const cities = await GeoDataService.getCitiesByStateId(stateId);
      return cities;
    },
    enabled: !!stateId,
  });
};

export const useToggleCountryStatus = () => {
  const queryClient = new QueryClient();
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return await GeoDataService.updateCountry(id, { is_active: isActive });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['geo', 'countries'] });
      toast({
        title: 'Country Status Updated',
        description: 'Country status has been successfully updated.',
        variant: 'success',
      });
    },
    onError: (error) => {
      logger.error('Failed to toggle country status', { error }, 'data');
      toast({
        title: 'Error',
        description: `Failed to update country status: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

export const useToggleStateStatus = () => {
  const queryClient = new QueryClient();
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return await GeoDataService.updateState(id, { is_active: isActive });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['geo', 'states'] });
      toast({
        title: 'State Status Updated',
        description: 'State status has been successfully updated.',
        variant: 'success',
      });
    },
    onError: (error) => {
      logger.error('Failed to toggle state status', { error }, 'data');
      toast({
        title: 'Error',
        description: `Failed to update state status: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

export const useToggleCityStatus = () => {
  const queryClient = new QueryClient();
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return await GeoDataService.updateCity(id, { is_active: isActive });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['geo', 'cities'] });
      toast({
        title: 'City Status Updated',
        description: 'City status has been successfully updated.',
        variant: 'success',
      });
    },
    onError: (error) => {
      logger.error('Failed to toggle city status', { error }, 'data');
      toast({
        title: 'Error',
        description: `Failed to update city status: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};
